/**
 * API Authentication Utilities
 * 
 * Provides helper functions to get Firebase auth tokens and create
 * authenticated request headers for API calls.
 * 
 * Usage with axios:
 *   const headers = await getAuthHeaders();
 *   await axios.post('/api/endpoint', data, { headers });
 * 
 * Usage with fetch:
 *   const response = await authFetch('/api/endpoint', {
 *     method: 'POST',
 *     body: JSON.stringify(data),
 *   });
 */

import { auth } from './firebaseConfig';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getApiBaseUrl, isNativePlatform } from '../utils/apiConfig';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';

/**
 * Resolve API URL for native platforms.
 * - For relative /api/ paths, prepends the appropriate base URL from apiConfig
 * - For absolute URLs, returns as-is
 */
function resolveApiUrl(url: string): string {
  if (url.startsWith('/api/')) {
    const baseUrl = getApiBaseUrl();
    const endpoint = url.substring(4); // Remove /api prefix, keep the slash
    return `${baseUrl}${endpoint}`;
  }
  return url;
}

/** Promise that resolves when auth state is ready (with a valid user) */
let authReadyPromise: Promise<User | null> | null = null;

/**
 * Wait for Firebase auth to be initialized.
 * Returns the current user (or null if not signed in).
 * 
 * Note: This now always checks auth.currentUser first to handle
 * cases where the user signs in after the initial auth state check.
 */
function waitForAuth(): Promise<User | null> {
  // Always check current user first - handles sign-in after initial load
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }
  
  // If we already have a pending promise, return it
  if (authReadyPromise) {
    return authReadyPromise;
  }
  
  // Wait for the first auth state change
  authReadyPromise = new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      // Clear cache if no user, so we re-check on next call
      if (!user) {
        authReadyPromise = null;
      }
      resolve(user);
    });
  });
  
  return authReadyPromise;
}

/**
 * Get the Firebase ID token for the current user.
 * Waits for auth state to be initialized before checking.
 * Returns null if no user is signed in.
 */
export async function getIdToken(): Promise<string | null> {
  // Wait for auth to be ready before checking currentUser
  const user = await waitForAuth();
  
  if (!user) {
    return null;
  }
  
  try {
    // Get token (don't force refresh unless needed)
    const token = await user.getIdToken(/* forceRefresh */ false);
    return token;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

/**
 * Get headers object with auth token for API calls.
 * 
 * @returns Headers object with Content-Type and X-Firebase-Auth (if user is signed in)
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  const token = await getIdToken();
  if (token) {
    // Use X-Firebase-Auth instead of Authorization to bypass Azure SWA's auth interception
    // Azure SWA replaces the Authorization header with its own token
    headers['X-Firebase-Auth'] = token;
  }
  
  return headers;
}

/**
 * Get headers for multipart/form-data requests (e.g., file uploads).
 * Note: Don't set Content-Type - axios will set it with boundary automatically.
 */
export async function getAuthHeadersForFormData(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  
  const token = await getIdToken();
  if (token) {
    // Use X-Firebase-Auth instead of Authorization to bypass Azure SWA token replacement
    headers['X-Firebase-Auth'] = token;
  }
  
  return headers;
}

/**
 * Get a fresh ID token, forcing refresh from Firebase.
 */
async function getFreshIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  
  try {
    // Force refresh to get a new token
    const token = await user.getIdToken(/* forceRefresh */ true);
    return token;
  } catch (error) {
    console.error('Failed to refresh ID token:', error);
    return null;
  }
}

/** Delay helper for retry backoff */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if an error or response indicates a retriable condition.
 * Handles cold start timeouts, network errors, and server errors.
 */
function isRetriableError(response: Response | null, error?: unknown): boolean {
  // Network error (fetch threw)
  if (error) {
    return true;
  }
  
  // No response somehow
  if (!response) {
    return true;
  }
  
  // Server errors (5xx) - often cold start or temporary issues
  if (response.status >= 500 && response.status < 600) {
    return true;
  }
  
  // Gateway timeout, service unavailable
  if (response.status === 408 || response.status === 429) {
    return true;
  }
  
  return false;
}

/**
 * Authenticated fetch wrapper with automatic retry for:
 * - 401 Unauthorized: Refreshes Firebase token and retries
 * - Network errors: Retries with exponential backoff (cold start handling)
 * - 5xx Server errors: Retries with exponential backoff
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (method, body, headers, etc.)
 * @param maxRetries - Maximum retry attempts for network/server errors (default: 2)
 * @returns Fetch Response
 */
export async function authFetch(
  url: string,
  options: RequestInit = {},
  maxRetries: number = 2
): Promise<Response> {
  // Resolve API URL for native platforms
  const resolvedUrl = resolveApiUrl(url);
  
  /**
   * Convert CapacitorHttp response to a standard Fetch Response
   */
  const httpResponseToFetchResponse = (httpResponse: HttpResponse): Response => {
    const body = typeof httpResponse.data === 'string' 
      ? httpResponse.data 
      : JSON.stringify(httpResponse.data);
    return new Response(body, {
      status: httpResponse.status,
      headers: httpResponse.headers,
    });
  };
  
  const makeRequest = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    
    // Copy existing headers
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }
    
    // Add Content-Type if not set and body is JSON
    if (!headers['Content-Type'] && !headers['content-type'] && options.body && typeof options.body === 'string') {
      headers['Content-Type'] = 'application/json';
    }
    
    // Use X-Firebase-Auth instead of Authorization to bypass Azure SWA token replacement
    if (token) {
      headers['X-Firebase-Auth'] = token;
    }
    
    // Use CapacitorHttp on native platforms to bypass CORS
    // This is called manually so it doesn't interfere with Firestore's fetch
    if (isNativePlatform()) {
      try {
        const httpResponse = await CapacitorHttp.request({
          url: resolvedUrl,
          method: (options.method || 'GET') as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
          headers,
          data: options.body ? JSON.parse(options.body as string) : undefined,
        });
        return httpResponseToFetchResponse(httpResponse);
      } catch (error) {
        console.error('CapacitorHttp request failed:', error);
        throw error;
      }
    }
    
    // Use standard fetch on web
    return fetch(resolvedUrl, {
      ...options,
      headers,
    });
  };

  let token = await getIdToken();
  let response: Response | null = null;
  let lastError: unknown = null;
  
  // Retry loop for network errors and server errors (cold start handling)
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      response = await makeRequest(token);
      lastError = null;
      
      // If 401 Unauthorized, try refreshing the token and retry once
      if (response.status === 401 && auth.currentUser) {
        console.log('Got 401, attempting token refresh and retry...');
        token = await getFreshIdToken();
        if (token) {
          response = await makeRequest(token);
        }
        // Don't retry 401s further - either token works or user needs re-login
        break;
      }
      
      // Check if we should retry (server error, cold start, etc.)
      if (isRetriableError(response, null) && attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), 5000); // 1s, 2s, max 5s
        console.log(`Server error ${response.status}, retrying in ${backoffMs}ms (attempt ${attempt + 1}/${maxRetries})...`);
        await delay(backoffMs);
        continue;
      }
      
      // Success or non-retriable error
      break;
      
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), 5000);
        console.log(`Network error, retrying in ${backoffMs}ms (attempt ${attempt + 1}/${maxRetries})...`, error);
        await delay(backoffMs);
        continue;
      }
      
      // Max retries exhausted, throw the error
      console.error('authFetch failed after max retries:', error);
      throw error;
    }
  }
  
  // If we somehow have no response (shouldn't happen), throw
  if (!response) {
    throw lastError || new Error('authFetch: No response received');
  }
  
  return response;
}

/**
 * Check if the current user is authenticated.
 */
export function isAuthenticated(): boolean {
  return !!auth.currentUser;
}

/**
 * Get the current user's UID, or null if not signed in.
 */
export function getCurrentUserId(): string | null {
  return auth.currentUser?.uid || null;
}
